import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {Task, TaskControllerService} from "../../../api";
import {Router} from "@angular/router";
import {ConnectionPositionPair} from "@angular/cdk/overlay";
import {TaskDetailsService} from "../../../services/tasks/task-details.service";
import {Subject} from "rxjs";
import {
  faArrowDownToBracket,
  faArrowUpFromBracket,
  faArrowUpRightFromSquare,
  faCalendarDays,
  faCircle,
  faCircle as faCircleRegular,
  faCircleCheck,
  faCircleExclamation,
  faCircleHalfStroke,
  faCircleSmall,
  faCopy,
  faEllipsisVertical,
  faMessages,
  faTrash
} from "@fortawesome/pro-solid-svg-icons";
import {MatDialog} from "@angular/material/dialog";
import {TaskDeleteDialog} from "../task-detail/task-detail.component";
import {faIfc} from "../../util/icons/custom-icons";
import {StateConfigService} from "../../../services/state/state-config.service";
import { NgClass } from "@angular/common";

import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {PriorityPipe} from "../../../pipes/priority.pipe";
import {
  BicButtonComponent,
  BicDropdownComponent,
  BicDropdownElementComponent,
  BicDropdownTriggerDirective,
  BicPopupComponent,
  TooltipDirective
} from "@buildinginformationcloud/bic-ui-components";
import {RoleAndPrivilegesPipe} from "../../../pipes/role-and-privileges.pipe";

@Component({
  selector: 'app-task-card',
  templateUrl: './task-card.component.html',
  styleUrls: ['./task-card.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgClass,
    FaIconComponent,
    PriorityPipe,
    BicDropdownComponent,
    BicDropdownElementComponent,
    BicDropdownTriggerDirective,
    TooltipDirective,
    BicPopupComponent,
    BicButtonComponent,
    RoleAndPrivilegesPipe
]
})
export class TaskCardComponent implements OnInit, AfterViewInit, OnDestroy {
  taskDetailService = inject(TaskDetailsService);
  stateConfigService = inject(StateConfigService);
  private dialog = inject(MatDialog);
  private taskControllerService = inject(TaskControllerService);
  private router = inject(Router);
  private taskDetailsService = inject(TaskDetailsService);


  @Input() task!: Task;
  @Input() selected: boolean = false;
  @Input() selectionMode = false;
  @Output() taskChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() exportBCFClicked: EventEmitter<Task> = new EventEmitter<Task>();
  @Output() duplicateTask: EventEmitter<Task> = new EventEmitter<Task>();
  taskChangedEvenet: Subject<Task> = new Subject<Task>();
  isOpen = false;
  showStatus = true;

  @ViewChild('metaRow') metaRow!: ElementRef<HTMLDivElement>;
  @ViewChild('statusSection') statusSection!: ElementRef<HTMLDivElement>;
  @ViewChild('assignedRow') assignedRow!: ElementRef<HTMLDivElement>;
  @ViewChild('assignedName') assignedName!: ElementRef<HTMLDivElement>;

  private resizeObserver: ResizeObserver | null = null;
  private zone = inject(NgZone);

  positions: ConnectionPositionPair[] = [
    {
      originX: 'end',
      originY: 'center',
      overlayX: 'start',
      overlayY: 'center'
    }
  ];


  ngOnInit(): void {
    this.taskDetailService.currentTaskChanged.subscribe(task => {
      if (task && task.id === this.task.id) {
        this.task = task;
        this.taskChange.emit(this.task);
      }
    });
  }

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.zone.runOutsideAngular(() => {
        this.checkStatusFit();
        this.checkAssignedNameFit();
      });
    });
    if (this.metaRow?.nativeElement) {
      this.resizeObserver.observe(this.metaRow.nativeElement);
    }
    if (this.assignedRow?.nativeElement) {
      this.resizeObserver.observe(this.assignedRow.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  private checkStatusFit(): void {
    if (!this.metaRow?.nativeElement || !this.statusSection?.nativeElement) return;

    const statusEl = this.statusSection.nativeElement;
    const wasHidden = statusEl.style.display === 'none';
    statusEl.style.display = '';

    const container = this.metaRow.nativeElement;
    const overflows = container.scrollWidth > container.clientWidth;

    if (overflows && this.showStatus) {
      this.showStatus = false;
      statusEl.style.display = 'none';
    } else if (!overflows && !this.showStatus) {
      this.showStatus = true;
    } else if (wasHidden && !overflows) {
      this.showStatus = true;
    } else if (wasHidden) {
      statusEl.style.display = 'none';
    }
  }

  private checkAssignedNameFit(): void {
    if (!this.assignedRow?.nativeElement || !this.assignedName?.nativeElement) return;

    const nameEl = this.assignedName.nativeElement;
    const wasHidden = nameEl.style.display === 'none';
    nameEl.style.display = '';

    const container = this.assignedRow.nativeElement;
    const overflows = container.scrollWidth > container.clientWidth;

    if (overflows) {
      nameEl.style.display = 'none';
    } else if (wasHidden && !overflows) {
      // Name fits again
    }
  }

  taskChanged(task: Task) {
    this.task = task;
    this.taskChange.emit(task);

  }

  openTaskDetails() {
      this.taskDetailService.setOpenedTask(this.task);
  }

  openDeleteDialog() {
    let dialogRef = this.dialog.open(TaskDeleteDialog, {
      panelClass:"rounded-corners-dialog",
      data: {task: this.task}
    });

  }

  deleteTask() {
    this.taskControllerService.deleteTask(this.task.id!).subscribe(next => {
      this.taskDetailsService.currentTaskDeleted.next(this.task);
    });
  }


  navigateOnDoubleClick(taskID: string | undefined) {
    this.router.navigate(['/tasks', taskID]);
  }

  getDaysUntilDueDate(dueDate: Date): number {
    const currentDate = new Date();
    const due = new Date(dueDate);
    const timeDiff = due.getTime() - currentDate.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24));
  }

  getAbsoluteValue(value: number): number {
    return Math.abs(value);
  }

  duplicate() {
    let duplicatedTask = structuredClone(this.task)
    duplicatedTask.id = undefined;
    if (duplicatedTask && duplicatedTask) {
      duplicatedTask.title = (duplicatedTask.title ?? '') + ' (Kopie)';
    }
    if (duplicatedTask) {
      duplicatedTask.comment = [];
      duplicatedTask.id = undefined;
    }
    this.duplicateTask.emit(duplicatedTask);
  }

    protected readonly faEllipsisVertical = faEllipsisVertical;
  protected readonly faIfc = faIfc;
  protected readonly faCalendarDays = faCalendarDays;
  protected readonly faCircleExclamation = faCircleExclamation;
  protected readonly faArrowDownToBracket = faArrowDownToBracket;
  protected readonly faTrash = faTrash;
  protected readonly faArrowUpFromBracket = faArrowUpFromBracket;
  protected readonly faCopy = faCopy;
  protected readonly faArrowUpRightFromSquare = faArrowUpRightFromSquare;
  protected readonly faCircleSmall = faCircleSmall;
  protected readonly faCircleHalfStroke = faCircleHalfStroke;
  protected readonly faCircle = faCircle;
  protected readonly faCircleCheck = faCircleCheck;
  protected readonly faCircleRegular = faCircleRegular;
  protected readonly faMessages = faMessages;
}
